<template>
  <div
      v-if="show"
      class="modalOverlay tw-fixed tw-w-full tw-h-full tw-top-0 tw-left-0 tw-flex tw-justify-center tw-items-center tw-backdrop-blur-sm tw-z-[9999]"
      @click.self="close"
  >
    <div
        class="tw-bg-[#1a1d23] tw-px-6 tw-py-5 tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-text-center tw-max-w-md tw-w-full tw-mx-4 tw-shadow-2xl tw-border tw-border-gray-700"
    >
      <!-- Success Icon & Title -->
      <div class="tw-flex tw-flex-col tw-items-center tw-gap-1">
        <div class="tw-w-8 tw-h-8 tw-bg-green-900/30 tw-rounded-full tw-flex tw-items-center tw-justify-center">
          <svg class="tw-w-4 tw-h-4 tw-text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h3 class="tw-text-sm tw-font-semibold tw-text-white">
          {{ message }}
        </h3>
      </div>

      <!-- URL Section (visible only on step 2) -->
      <div
          v-if="step === 2"
          class="tw-bg-[#0f1115] tw-rounded-lg tw-border tw-border-gray-700 tw-py-[5px] tw-px-3"
      >
        <p class="tw-text-xs tw-text-gray-400 tw-mb-1.5">{{ $t('designUrl') }}</p>
        <div class="tw-grid tw-grid-cols-[1fr_auto] tw-gap-2.5 tw-items-center">
          <a
              :href="designUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-text-xs tw-font-mono tw-text-white tw-truncate hover:tw-text-[#FFEF46] tw-transition-colors tw-no-underline"
          >
            {{ designUrl }}
          </a>
          <div
              @click="copyUrl"
              class="tw-bg-gray-700 tw-rounded-lg tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-bg-gray-600 tw-transition-all"
              :title="$t('copyLink')"
          >
            <svg v-if="!copiedUrl" class="tw-w-3.5 tw-h-3.5 tw-text-gray-300" fill="none" stroke="currentColor"
                 viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
            <svg v-else class="tw-w-3.5 tw-h-3.5 tw-text-green-400" fill="none" stroke="currentColor"
                 viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Email Capture Step (Step 1) -->
      <div
          v-if="showQRCode && step === 1"
          class="tw-bg-[#0f1115] tw-rounded-lg tw-border tw-border-gray-700 tw-p-4 tw-flex tw-flex-col tw-gap-3 tw-text-left"
      >
        <p class="tw-text-sm tw-text-white tw-font-semibold">
          {{ $t('designSavedEmailTitle') }}
        </p>
        <p class="tw-text-xs tw-text-gray-400">
          {{ $t('designSavedEmailSubtitle') }}
        </p>

        <div class="tw-flex tw-flex-col tw-gap-2">
          <input
              v-model="email"
              type="email"
              :placeholder="$t('designSavedEmailPlaceholder')"
              class="tw-bg-[#05070a] tw-border tw-border-gray-700 focus:tw-border-[#FFEF46] tw-rounded-lg tw-text-xs tw-text-white tw-px-3 tw-py-2 tw-outline-none tw-transition-colors"
          />
          <p v-if="emailError" class="tw-text-[11px] tw-text-red-400">
            {{ emailError }}
          </p>

          <label class="tw-flex tw-items-start tw-gap-2 tw-text-[11px] tw-text-gray-400 tw-cursor-pointer">
            <input
                type="checkbox"
                v-model="marketingOptIn"
                class="tw-mt-[2px]"
            />
            <span v-html="$t('designSavedEmailMarketingOptIn')"></span>
          </label>

          <p v-if="sendError" class="tw-text-[11px] tw-text-orange-400">
            {{ sendError }}
          </p>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-2 tw-mt-1">
          <button
              class="tw-bg-[#FFEF46] tw-text-black tw-font-semibold tw-rounded-lg tw-text-sm tw-w-full tw-py-2.5 tw-cursor-pointer tw-flex tw-justify-center tw-items-center hover:tw-bg-[#FFE500] tw-transition-all disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
              @click="handleSendEmail"
              :disabled="sending"
          >
            <span v-if="!sending">{{ $t('designSavedEmailPrimaryCta') }}</span>
            <span v-else>{{ $t('designSavedEmailSending') }}</span>
          </button>

          <!-- <button
              class="tw-bg-transparent tw-border tw-border-gray-700 tw-text-gray-200 tw-font-medium tw-rounded-lg tw-text-xs tw-w-full tw-py-2 tw-cursor-pointer tw-flex tw-justify-center tw-items-center hover:tw-bg-[#151821] tw-transition-all"
              @click="goToQrStep"
          >
            {{ $t('designSavedEmailSkip') }}
          </button> -->
        </div>
      </div>

      <!-- QR Code Section (Step 2) -->
      <div v-if="showQRCode && step === 2"
           class="tw-bg-[#0f1115] tw-rounded-lg tw-p-4 tw-border tw-border-gray-700 tw-flex tw-flex-col tw-items-center tw-gap-2">
        <p class="tw-text-sm tw-text-white tw-uppercase tw-font-bold tw-tracking-wide">{{ $t('saveThisQRCode') }}</p>
        <p class="tw-text-[10px] tw-text-gray-500 tw-mt-[-4px]">{{ $t('clickQRToDownload') }}</p>
        <div
            class="tw-bg-white tw-p-2 tw-rounded-lg tw-cursor-pointer  tw-transition-colors qr-code-container"
            @click="downloadQRCode">
          <QrcodeVue3 :value="qrUrl"
                      :width="800"
                      :height="800"
                      :image="logoPath"
                      :imageOptions="{
                        hideBackgroundDots: true,
                        imageSize: 0.3,
                        margin: 15
                      }"
                      :dotsOptions="{
                        type: 'extra-rounded',
                        color: '#000000',
                        roundSize: true
                      }"
                      :cornersSquareOptions="{
                        type: 'extra-rounded',
                        color: '#000000'
                      }"
                      :cornersDotOptions="{
                        type: '',
                        color: '#000000'
                      }"
                      :fileExt="'png'"
                      ref="qrcode"/>
        </div>
        <div class="tw-mt-2 tw-text-center">
          <p class="tw-text-xs tw-font-mono tw-font-semibold tw-text-white tw-break-all">
            {{ jobId }}
          </p>
        </div>
      </div>


      <!-- Skip/Close Button -->
      <div
          class="tw-bg-[#0f1115] tw-border-2 tw-border-[#FFEF46] tw-text-white tw-font-semibold tw-rounded-lg tw-text-sm tw-w-full tw-py-2.5 tw-cursor-pointer tw-flex tw-justify-center tw-items-center hover:tw-bg-[#1a1d23] tw-transition-all tw-mt-2"
          @click="step === 1 ? goToQrStep() : close()"
      >
        {{ step === 1 ? $t('skip') : $t('close') }}
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue3 from 'qrcode-vue3';
import { getAssetPath } from '@/utils/assetPath';
import { sendDesignEmail } from '@/services/api';
import { trackEvent } from '@/utils/tracking';

export default {
  components: {
    QrcodeVue3
  },

  props: {
    jobId: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: 'Design has been saved!'
    },
    showQRCode: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: ''
    },
    autoCloseDelay: {
      type: Number,
      default: 0 // 0 means no auto-close
    },
    notificationSent: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      show: false,
      copiedJobId: false,
      copiedUrl: false,
      autoCloseTimer: null,
      step: 1,
      email: '',
      marketingOptIn: false,
      emailError: '',
      sendError: '',
      sending: false
    };
  },

  computed: {
    designUrl() {
      if (this.url) {
        return this.url;
      }
      // Generate URL from jobId
      const baseUrl = window.location.origin;
      return `${baseUrl}/${this.jobId}`;
    },
    qrUrl() {
      return this.designUrl;
    },
    logoPath() {
      return getAssetPath('/assets/images/solflareIcon.svg');
    }
  },

  methods: {
    open() {
      this.show = true;
      this.copiedJobId = false;
      this.copiedUrl = false;
      // If notification already sent, skip email step and go directly to QR code
      this.step = (this.showQRCode && !this.notificationSent) ? 1 : 2;
      this.email = '';
      this.marketingOptIn = false;
      this.emailError = '';
      this.sendError = '';
      this.sending = false;

      // Auto-close if configured
      if (this.autoCloseDelay > 0) {
        this.autoCloseTimer = setTimeout(() => {
          this.close(false);
        }, this.autoCloseDelay);
      }
    },

    close(userInitiated = true) {
      if (userInitiated) {
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "CloseModal",
          details: { modal: "DesignSavedModal" },
        });
      }
      this.show = false;
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
        this.autoCloseTimer = null;
      }
      this.$emit('closed');
    },

    goToQrStep() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "Skip",
        details: { modal: "DesignSavedModal" },
      });
      this.step = 2;
    },

    validateEmail(value) {
      if (!value) {
        return this.$t('designSavedEmailErrorRequired');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return this.$t('designSavedEmailErrorInvalid');
      }
      return '';
    },

    async handleSendEmail() {
      this.emailError = '';
      this.sendError = '';

      const error = this.validateEmail(this.email);
      if (error) {
        this.emailError = error;
        return;
      }

      this.sending = true;

      try {
        const success = await sendDesignEmail(this.jobId, {
          email: this.email,
          marketingOptIn: this.marketingOptIn,
          designUrl: this.designUrl
        });

        if (success) {
          trackEvent({
            eventName: "Customizer",
            eventType: "Submit",
            action: "SendDesignEmail",
            details: this.jobId ? { designId: this.jobId } : undefined,
          });
        } else {
          this.sendError = this.$t('designSavedEmailErrorGeneric');
        }
      } catch (e) {
        console.error('Failed to send design email:', e);
        this.sendError = this.$t('designSavedEmailErrorGeneric');
      } finally {
        this.sending = false;
        // Always show QR step as fail-safe
        this.step = 2;
      }
    },

    async copyJobId() {
      try {
        await navigator.clipboard.writeText(this.jobId);
        this.copiedJobId = true;
        setTimeout(() => {
          this.copiedJobId = false;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy job ID:', error);
      }
    },

    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.designUrl);
        this.copiedUrl = true;
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "CopyDesignUrl",
          details: this.jobId ? { designId: this.jobId } : undefined,
        });
        setTimeout(() => {
          this.copiedUrl = false;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy URL:', error);
      }
    },

    async downloadQRCode() {
      try {
        // Wait for next tick to ensure QR code is fully rendered
        await this.$nextTick();

        // qrcode-vue3 renders an img element
        const qrComponent = this.$refs.qrcode;

        if (qrComponent) {
          const img = qrComponent.$el?.querySelector('img');

          if (img && img.src) {
            // Download the QR code image using the img src
            const link = document.createElement('a');
            link.href = img.src;
            link.download = `qr-${this.jobId}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
      } catch (error) {
        console.error('Failed to download QR code:', error);
      }
    }
  },

  beforeUnmount() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }
  }
};
</script>

<style lang="scss" scoped>
#cardDesigner {
  .modalOverlay {
    z-index: 9999;
    background: linear-gradient(135deg, rgba(255, 239, 70, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
    animation: fadeIn 0.2s ease-out;

    > div {
      animation: slideUp 0.3s ease-out;
    }
  }

  .qr-code-container {
    width: 240px;
    height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    :deep(img) {
      width: 240px;
      height: 240px;
      object-fit: contain;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
}
</style>
